import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, Play, Pause, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import ChordViewer from "@/components/ChordViewer";
import { Metronome, Pad } from "@/lib/audio";
import { LogoMark } from "@/components/Logo";

export default function StagePage() {
  const { songId } = useParams();
  const nav = useNavigate();
  const [songs, setSongs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [met, setMet] = useState(false);
  const [padOn, setPadOn] = useState(false);
  const metRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    api.get("/songs").then((r) => {
      setSongs(r.data);
      if (songId) { const i = r.data.findIndex((s) => s.id === songId); if (i >= 0) setIdx(i); }
    });
    metRef.current = new Metronome();
    padRef.current = new Pad();
    // keep screen awake where supported
    let wl; if (navigator.wakeLock) navigator.wakeLock.request("screen").then((w) => (wl = w)).catch(() => {});
    return () => { metRef.current?.stop(); padRef.current?.stop(0.3); if (wl) wl.release?.(); };
  }, []); // eslint-disable-line

  const song = songs[idx];
  const toggleMet = () => {
    const m = metRef.current;
    if (met) { m.stop(); setMet(false); }
    else { m.bpm = song?.bpm || 72; m.beats = 4; m.sound = "click"; m.start(); setMet(true); }
  };
  const togglePad = () => {
    const p = padRef.current;
    if (padOn) { p.stop(1.5); setPadOn(false); }
    else { p.setVolume(0.5); p.play(song?.arrangement_key || "E", "Warm", 2); setPadOn(true); }
  };

  if (!song) return <div className="stage-canvas min-h-screen flex items-center justify-center"><LogoMark size={56} /></div>;

  return (
    <div className="stage-canvas min-h-screen text-white no-select">
      <div className="sticky top-0 z-20 bg-[#0A0F1D]/90 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => nav(-1)} data-testid="stage-exit" className="p-2 rounded-lg bg-white/5 border border-white/10"><X className="w-5 h-5" /></button>
          <div className="min-w-0">
            <div className="font-display font-bold text-lg sm:text-2xl truncate">{song.title}</div>
            <div className="text-xs text-[#8A97AC] font-mono">{song.artist}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-lg bg-[#E5C378]/15 text-[#FFD56B] font-mono font-bold border border-[#E5C378]/30">{song.arrangement_key}</span>
          <span className="px-3 py-1.5 rounded-lg bg-white/5 text-white font-mono border border-white/10">{song.bpm} BPM</span>
          <span className="hidden sm:inline px-3 py-1.5 rounded-lg bg-white/5 text-white font-mono border border-white/10">{song.time_signature}</span>
        </div>
      </div>

      <div className="px-2 sm:px-6 py-4">
        <ChordViewer key={song.id} song={song} stage />
      </div>

      {/* stage controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0A0F1D]/95 backdrop-blur border-t border-white/10 px-3 py-3 flex items-center justify-between gap-2">
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} data-testid="stage-prev" className="h-14 px-4 rounded-xl bg-white/5 border border-white/10 disabled:opacity-30 flex items-center gap-1"><ChevronLeft className="w-6 h-6" /></button>
        <button onClick={toggleMet} data-testid="stage-metronome" className={`h-14 flex-1 max-w-[130px] rounded-xl font-bold border-2 flex items-center justify-center gap-2 ${met ? "bg-[#E5C378] text-[#03060C] border-white" : "bg-[#080C14] text-[#FFD56B] border-[#FFD56B]"}`}>
          {met ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />} CLICK
        </button>
        <button onClick={togglePad} data-testid="stage-pad" className={`h-14 flex-1 max-w-[130px] rounded-xl font-bold border-2 flex items-center justify-center gap-2 ${padOn ? "bg-[#4B7BE5] text-white border-white" : "bg-[#080C14] text-[#4E97FF] border-[#4E97FF]"}`}>
          PAD {song.arrangement_key}
        </button>
        <div className="hidden sm:flex items-center px-3 text-xs text-[#6E7D97] font-mono">{idx + 1} / {songs.length}</div>
        <button onClick={() => setIdx((i) => Math.min(songs.length - 1, i + 1))} disabled={idx >= songs.length - 1} data-testid="stage-next" className="h-14 px-4 rounded-xl bg-white/5 border border-white/10 disabled:opacity-30 flex items-center gap-1"><ChevronRight className="w-6 h-6" /></button>
      </div>
    </div>
  );
}
